/* eslint-disable no-restricted-globals */
import type { FetcherOptions, InputRequest } from "./types"

import { logServerFetch } from "@/modules/monitoring/logServerFetch"
import { measurePromiseTiming } from "@/modules/monitoring/measurePromiseTiming"

import { logFetchPrefix } from "./constants"

const getRequestUrl = (request: InputRequest): string => {
  if (request instanceof Request) {
    return request.url
  }

  if (request instanceof URL) {
    return request.href
  }

  return request
}

/**
 * This is the general fetcher of the project.
 * If you are in a local environment, on client-side and trying to call our internal staging API (apigw or monolith),
 * it will update the configuration, so you are able to use authenticated endpoints.
 * For all other cases, it will use the next "native" isomorphic measurePromiseTiming(`fetch ${requestUrl}`,  fetch method.)
 *
 * If env is local and client-side, we need to force "include" for credentials field
 * so that cookie is added to cross-origin requests. "include" tells browsers to include cookies
 * in both same- and cross-origin requests, and always use any cookies sent back in responses.
 * Notice that "same-origin" is the default value.
 * NB: we keep NEXT_PUBLIC_ENV condition apart for better cleaning for deployed envs.
 *
 * @param input basically the URL or string you want to call (you can also use a Request input)
 * @param init options that are necessary to perform the measurePromiseTiming(`fetch ${requestUrl}`,  fetch action, not required)
 * @param nextRequest that permits to create server side requests (for authentication)
 * @param withDefaultContentType that permits to cancel the default header "content-type: application/json" to be used in the request
 *
 * @return a promise resolving as a Response object
 */

export const fetcher = async (input: InputRequest, init?: RequestInit, options?: FetcherOptions): Promise<Response> => {
  const { serviceDomain } = options || {}
  const requestUrl = getRequestUrl(input)

  if (!serviceDomain || serviceDomain === "INTERNAL_NEXT_API") {
    if (typeof window !== "undefined") {
      return measurePromiseTiming(`fetch ${requestUrl}`, fetch(input, init))
    }

    return logServerFetch(`${logFetchPrefix}${requestUrl}`, fetch(input, init))
  }

  // Client side
  if (typeof window !== "undefined") {
    const { internalClientOptions } = await import("./client/internalClientOptions")

    return measurePromiseTiming(
      `fetch ${requestUrl}`,
      fetch(input, await internalClientOptions(init || {}, options || {}))
    )
  }

  // Server side
  const { internalServerOptions } = await import("./server/internalServerOptions")
  const internalServerOpts = internalServerOptions(init || {}, options || {})

  // Keep this to facilitate debugging of SS API calls locally
  // console.log("Server-side fetch details", { url: input, internalServerOptions: internalServerOpts })
  return logServerFetch(`${logFetchPrefix}${requestUrl}`, fetch(input, internalServerOpts))
}
